import './style.css';
import { generateChart } from './engine/ziwei/algorithm';
import type { Palace, Star, ZiweiChart } from './engine/ziwei/types';
import { BRANCHES, STEMS } from './engine/ziwei/constants';
import { branchLabel, toBirthInfo } from './birth';
import {
  bindNihaiPanel,
  getNihaiEntries,
  hasNihaiCorpus,
  loadNihaiCorpus,
  ncBannerHtml,
  renderNihaiPanel,
  setNihaiKeyword,
  escapeHtml,
} from './nihai';
import {
  guideSearch,
  keywordForNihaiTab,
  type GuideHit,
  type GuideTerm,
} from './nihai-guide';

/** 上游引擎宮名係簡體；UI 顯示繁體 */
const PALACE_ZH: Record<string, string> = {
  '命宫': '命宮', '兄弟': '兄弟宮', '夫妻': '夫妻宮', '子女': '子女宮',
  '财帛': '財帛宮', '疾厄': '疾厄宮', '迁移': '遷移宮', '仆役': '交友宮',
  '官禄': '官祿宮', '田宅': '田宅宮', '福德': '福德宮', '父母': '父母宮',
};
function palaceLabel(name: string): string {
  return PALACE_ZH[name] ?? name;
}

const app = document.querySelector<HTMLDivElement>('#app')!;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function nowLocalParts() {
  const d = new Date();
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
  };
}

function toDatetimeLocalValue(y: number, m: number, d: number, h: number, mi: number): string {
  return `${y}-${pad(m)}-${pad(d)}T${pad(h)}:${pad(mi)}`;
}

function parseDatetimeLocal(v: string): { year: number; month: number; day: number; hour: number; minute: number } | null {
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  return { year: +m[1], month: +m[2], day: +m[3], hour: +m[4], minute: +m[5] };
}

/** 地支 → 4×4 宮格位置（傳統盤：巳午未申 上排…） */
const BRANCH_GRID_POS: Record<number, [number, number]> = {
  5: [1, 1], 6: [1, 2], 7: [1, 3], 8: [1, 4],
  4: [2, 1],                         9: [2, 4],
  3: [3, 1],                        10: [3, 4],
  2: [4, 1], 1: [4, 2], 0: [4, 3], 11: [4, 4],
};

const n0 = nowLocalParts();
let state = {
  ...n0,
  gender: 'male' as 'male' | 'female',
  chart: null as ZiweiChart | null,
  error: '' as string,
  tab: 'chart' as 'chart' | 'nihai',
  askQuestion: '' as string,
  askHits: null as GuideHit[] | null,
  askQuestionTerms: [] as GuideTerm[],
  askChartTerms: [] as GuideTerm[],
  askError: '' as string,
  askLoading: false,
};

function renderStar(s: Star): string {
  const cls = s.type === 'major' ? 'major' : s.type === 'lucky' ? 'lucky' : s.type === 'sha' ? 'sha' : '';
  const br = s.brightnessRaw ? `<span class="bright">${s.brightnessRaw}</span>` : '';
  const sh = s.siHua ? `<span class="sihua ${s.siHua}">${s.siHua}</span>` : '';
  return `<span class="star ${cls}">${s.name}${br}${sh}</span>`;
}

function renderPalace(p: Palace): string {
  const gz = `${STEMS[p.stem] ?? ''}${BRANCHES[p.branch] ?? ''}`;
  const flags = [
    p.isMingGong ? '命' : '',
    p.isShenGong ? '身' : '',
    p.isCurrentDaXian ? '限' : '',
  ].filter(Boolean).join('·');
  const majors = p.stars.filter((s) => s.type === 'major');
  const others = p.stars.filter((s) => s.type !== 'major');
  const age = p.daXianAge ? `${p.daXianAge[0]}–${p.daXianAge[1]}歲` : '';
  const borrow =
    p.isEmpty && p.borrowedStars?.length
      ? `<div class="note">空：借${palaceLabel(p.borrowedFromName ?? '')}（${p.borrowedStars.join('')}）</div>`
      : '';
  return `
    <div class="palace ${p.isMingGong ? 'ming' : ''} ${p.isShenGong ? 'shen' : ''}" style="grid-column:${BRANCH_GRID_POS[p.branch][1]};grid-row:${BRANCH_GRID_POS[p.branch][0]}">
      <div class="ph">
        <span class="pname">${palaceLabel(p.name)}${flags ? ` · ${flags}` : ''}</span>
        <span class="pgz">${gz}</span>
      </div>
      <div class="stars">${majors.map(renderStar).join('')}</div>
      <div class="stars">${others.map(renderStar).join('')}</div>
      ${age ? `<div class="pgz">${age}</div>` : ''}
      ${borrow}
    </div>`;
}

function renderChart(chart: ZiweiChart): string {
  const bi = chart.birthInfo;
  const orig = bi.origInput;
  const lunar = chart.lunarInfo;
  const pillars = lunar.fourPillars?.join(' ') ?? '';
  const lateNote =
    orig && (orig.day !== bi.day || orig.month !== bi.month || orig.year !== bi.year)
      ? `（晚子時已換日：輸入 ${orig.year}-${pad(orig.month)}-${pad(orig.day)} ${pad(orig.clockHour)}:${pad(orig.clockMinute)} → 排盤日 ${bi.year}-${pad(bi.month)}-${pad(bi.day)}）`
      : '';

  const palacesHtml = chart.palaces.map(renderPalace).join('');
  const center = `
    <div class="palace center">
      <div><strong>${chart.yinYangGender ?? ''} · ${chart.wuxingJuName}</strong></div>
      <div>命主 ${chart.mingZhu ?? '—'}　身主 ${chart.shenZhu ?? '—'}</div>
      <div>虛歲 ${chart.currentAge}</div>
      <div class="pgz">${pillars}</div>
    </div>`;

  const dx = chart.daXians
    .map(
      (d, i) =>
        `<span class="dx ${i === chart.currentDaXianIndex ? 'cur' : ''}">${d.startAge}–${d.endAge} ${palaceLabel(d.palaceName)}</span>`,
    )
    .join('');

  return `
    <section class="panel">
      <h2>本命摘要</h2>
      <div class="meta-grid">
        <div><div class="k">排盤日／時辰</div><div class="v">${bi.year}-${pad(bi.month)}-${pad(bi.day)}　${branchLabel(bi.hour)}</div></div>
        <div><div class="k">性別</div><div class="v">${bi.gender === 'male' ? '男' : '女'}</div></div>
        <div><div class="k">五行局</div><div class="v">${chart.wuxingJuName}</div></div>
        <div><div class="k">命宮／身宮</div><div class="v">${BRANCHES[chart.mingGongBranch]}／${BRANCHES[chart.shenGongBranch]}</div></div>
        <div><div class="k">農曆</div><div class="v">${lunar.lunarYear}年${lunar.isLeapMonth ? '閏' : ''}${Math.abs(lunar.lunarMonth)}月${lunar.lunarDay}日</div></div>
        <div><div class="k">節氣四柱</div><div class="v">${pillars || '—'}</div></div>
      </div>
      <p class="note">${lateNote}</p>
    </section>
    <section class="panel">
      <h2>十二宮盤面</h2>
      <div class="chart-grid">
        ${palacesHtml}
        ${center}
      </div>
    </section>
    <section class="panel">
      <h2>大限</h2>
      <div class="dx-list">${dx || '—'}</div>
    </section>
    <section class="panel stub">
      <strong>解讀數據集另接</strong> — 本原型只跑 MIT 排盤引擎，<em>唔</em>內嵌 v4.0 56GB 解讀。
      倪師《天紀》原話（CC BY-NC-SA）已獨立放喺「倪師原話」分頁，僅供個人非商業使用。
    </section>`;
}

function snippet(text: string, max = 110): string {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length <= max ? t : `${t.slice(0, max)}…`;
}

function renderAskResults(): string {
  if (state.askHits === null) return '';
  const hits = state.askHits;
  if (!hits.length) {
    return `<p class="note">搵唔到足夠重疊嘅原話。試下寫星曜、宮位、四化（例如「夫妻宮化忌」「財帛有武曲」）。</p>`;
  }
  const cards = hits
    .map((h) => {
      const e = h.entry;
      const tags = [e.bookLabel, e.h2, e.h3]
        .filter(Boolean)
        .map((t) => `<span class="tag">${escapeHtml(t)}</span>`)
        .join('');
      const why = h.reasons.map((r) => `<span class="why">${escapeHtml(r)}</span>`).join('');
      return `
        <article class="quote-card guide-card">
          <div class="quote-meta">
            <span class="lec">${escapeHtml(e.lecture)} · ${escapeHtml(e.lectureTitle || '')}</span>
            <a class="ts" href="${escapeHtml(e.url)}" target="_blank" rel="noopener" title="Bilibili 原片">${escapeHtml(e.t)} ↗</a>
          </div>
          <blockquote>「${escapeHtml(snippet(e.quote))}」</blockquote>
          <div class="why-row">${why}</div>
          <div class="tags">${tags}</div>
        </article>`;
    })
    .join('');

  const chartN = state.askChartTerms.length;
  const qN = state.askQuestionTerms.length;
  return `
    <div class="guide-results" id="ask-results">
      <p class="note">
        顯示 ${hits.length} 條導航結果（問題詞 ${qN} · 對盤詞 ${chartN}）。
        呢啲係<strong>原話導航／指引</strong>（關鍵字＋盤面標籤重疊），<em>唔係</em>自動解盤或 AI 批命。
      </p>
      <div class="quote-list">${cards}</div>
      <div class="btn-row" style="margin-top:0.75rem">
        <button type="button" class="secondary" id="btn-ask-to-nihai">喺原話分頁睇</button>
      </div>
    </div>`;
}

function renderAskPanel(): string {
  const hasChart = !!state.chart;
  const banner = hasChart
    ? ''
    : `<p class="ask-banner" role="status">未排盤：只按問題搜；排盤後會加對盤加權。</p>`;
  return `
    <section class="panel ask-panel" id="ask-panel">
      <h2>問盤／題問</h2>
      <p class="note">輸入你想了解嘅問題（例如「夫妻宮化忌點睇」「今年大限財帛」）。系統會用<strong>問題關鍵字</strong>${hasChart ? '＋<strong>而家盤面標籤</strong>' : ''}，喺約 1848 條倪師原話入面指引你睇邊段。自用／非商業；唔構成預測。</p>
      ${banner}
      <label class="ask-label">你嘅問題
        <textarea id="ask-q" rows="3" placeholder="例如：命宮有紫微，夫妻宮化忌，婚姻點理解？">${escapeHtml(state.askQuestion)}</textarea>
      </label>
      <div class="btn-row" style="margin-top:0.55rem">
        <button type="button" id="btn-ask" ${state.askLoading ? 'disabled' : ''}>${state.askLoading ? '搜緊…' : '搵相關原話'}</button>
      </div>
      ${state.askError ? `<p class="err">${escapeHtml(state.askError)}</p>` : ''}
      ${renderAskResults()}
    </section>`;
}

function renderChartTab(): string {
  const dtVal = toDatetimeLocalValue(state.year, state.month, state.day, state.hour, state.minute);
  return `
    <section class="panel">
      <h2>出生資料</h2>
      <div class="controls">
        <label>公曆日時
          <input id="dt" type="datetime-local" value="${dtVal}" />
        </label>
        <label>性別
          <select id="gender">
            <option value="male" ${state.gender === 'male' ? 'selected' : ''}>男</option>
            <option value="female" ${state.gender === 'female' ? 'selected' : ''}>女</option>
          </select>
        </label>
        <div class="btn-row">
          <button type="button" id="btn-chart">排盤</button>
          <button type="button" class="secondary" id="btn-now">而家</button>
        </div>
      </div>
      <p class="note">曆法備註：引擎用農曆正月初一換年；閏月默認「月中分界」(split)。本頁唔做真太陽時／出生地經度校正（等同東經 120° 鐘點）。23:00–23:59 晚子時會自動換去次日。</p>
      ${state.error ? `<p class="err">${state.error}</p>` : ''}
    </section>
    ${renderAskPanel()}
    ${state.chart ? renderChart(state.chart) : '<section class="panel stub">輸入日時後撳「排盤」。</section>'}`;
}

function footerHtml(): string {
  return `
    <footer class="attr">
      <p>
        排盤引擎改編自 <a href="https://github.com/Renhuai123/ziwei-doushu" target="_blank" rel="noopener">Renhuai123/ziwei-doushu</a>（<strong>MIT</strong>，作者：王多鱼AI／紫微研究）。
        另依賴 iztro、lunar-javascript。
      </p>
      <p>
        <strong>倪師原話</strong>整理自
        <a href="https://github.com/Renhuai123/ziwei-doushu/tree/main/docs/tianji-ziwei" target="_blank" rel="noopener">ziwei-doushu/docs/tianji-ziwei</a>
        ＋
        <a href="https://github.com/Renhuai123/nihai-tianji-corpus" target="_blank" rel="noopener">nihai-tianji-corpus</a>，
        授權 <a href="https://creativecommons.org/licenses/by-nc-sa/4.0/deed.zh-hans" target="_blank" rel="noopener">CC BY-NC-SA 4.0</a>：
        <strong>自用／非商業；不可商用</strong>；須署名；改編須相同方式共享。見站內 <code>LICENSE.tianji-ziwei.md</code>。
      </p>
      <p>內容僅供學習／娛樂參考，唔構成任何預測或建議。本站<strong>唔</strong>含 v4.0 解讀數據集。題問結果只係原話導航，唔係自動解盤。</p>
    </footer>`;
}

async function runAsk(): Promise<void> {
  const q = state.askQuestion.trim();
  if (!q) {
    state.askError = '請先輸入問題';
    state.askHits = null;
    render();
    return;
  }
  state.askLoading = true;
  state.askError = '';
  render();
  try {
    if (!hasNihaiCorpus()) {
      await loadNihaiCorpus();
    }
    const entries = getNihaiEntries();
    if (!entries.length) throw new Error('原話語料未載入');
    const { hits, questionTerms, chartTerms } = guideSearch(entries, q, state.chart, 12);
    state.askHits = hits;
    state.askQuestionTerms = questionTerms;
    state.askChartTerms = chartTerms;
    state.askError = '';
  } catch (e) {
    state.askError = e instanceof Error ? e.message : String(e);
    state.askHits = null;
  } finally {
    state.askLoading = false;
    render();
    document.getElementById('ask-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function render(): void {
  const tab = state.tab;
  app.innerHTML = `
    <header>
      <h1>Topwin 紫微斗數</h1>
      <p class="subtitle">開源 <a href="https://github.com/Renhuai123/ziwei-doushu" target="_blank" rel="noopener">ziwei-doushu</a> 引擎（MIT）＋倪師原話（CC BY-NC-SA · 自用／非商業）· 時區建議 Asia/Hong_Kong</p>
      <nav class="tabs" aria-label="主要分頁">
        <button type="button" class="tab ${tab === 'chart' ? 'active' : ''}" data-tab="chart">排盤</button>
        <button type="button" class="tab ${tab === 'nihai' ? 'active' : ''}" data-tab="nihai">倪師原話</button>
      </nav>
    </header>
    ${tab === 'nihai' ? '' : ncBannerHtml()}
    <div id="tab-body">
      ${tab === 'chart' ? renderChartTab() : renderNihaiPanel(() => render())}
    </div>
    ${footerHtml()}
  `;

  app.querySelectorAll<HTMLButtonElement>('.tab[data-tab]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const next = btn.dataset.tab as 'chart' | 'nihai';
      if (next === state.tab) return;
      state.tab = next;
      render();
    });
  });

  if (tab === 'chart') {
    const dt = app.querySelector<HTMLInputElement>('#dt')!;
    const gender = app.querySelector<HTMLSelectElement>('#gender')!;
    const run = () => {
      const parsed = parseDatetimeLocal(dt.value);
      if (!parsed) {
        state.error = '日時格式唔啱';
        render();
        return;
      }
      state = { ...state, ...parsed, gender: gender.value as 'male' | 'female', error: '' };
      try {
        const info = toBirthInfo({
          year: state.year,
          month: state.month,
          day: state.day,
          hour: state.hour,
          minute: state.minute,
          gender: state.gender,
        });
        state.chart = generateChart(info);
        state.error = '';
      } catch (e) {
        state.chart = null;
        state.error = e instanceof Error ? e.message : String(e);
      }
      render();
    };
    app.querySelector('#btn-chart')!.addEventListener('click', run);
    app.querySelector('#btn-now')!.addEventListener('click', () => {
      const n = nowLocalParts();
      state = { ...state, ...n, error: '' };
      render();
      app.querySelector<HTMLButtonElement>('#btn-chart')?.click();
    });

    const askTa = app.querySelector<HTMLTextAreaElement>('#ask-q');
    askTa?.addEventListener('change', () => {
      state.askQuestion = askTa.value;
    });
    askTa?.addEventListener('input', () => {
      state.askQuestion = askTa.value;
    });
    app.querySelector('#btn-ask')?.addEventListener('click', () => {
      state.askQuestion = askTa?.value ?? state.askQuestion;
      void runAsk();
    });
    app.querySelector('#btn-ask-to-nihai')?.addEventListener('click', () => {
      const kw = keywordForNihaiTab(state.askQuestionTerms, state.askHits ?? []);
      setNihaiKeyword(kw || state.askQuestion.trim().slice(0, 20));
      state.tab = 'nihai';
      render();
    });
  } else {
    const body = app.querySelector<HTMLElement>('#tab-body')!;
    bindNihaiPanel(body, () => render());
    if (!hasNihaiCorpus()) {
      void loadNihaiCorpus()
        .then(() => render())
        .catch(() => render());
    }
  }
}

render();
